import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import './global.css';

const importAll = (r) => {
    let media = {};
    r.keys().map((item) => { 
      media[item.replace('./', '')] = r(item); 
      return media[item.replace('./', '')];
    });
    return media;
};

const images = importAll(require.context('../../public/images', false, /\.(png|jpe?g|svg)$/));
const videos = importAll(require.context('../../public/videos', false, /\.(mp4|webm)$/));

function Popup({ media, onClose }) {
    return (
        <div className="popup-overlay" onClick={onClose}>
            <div className="popup-content" onClick={(e) => e.stopPropagation()}>
                {media.src.endsWith('.mp4') || media.src.endsWith('.webm') ? (
                    <video controls className="popup-video" style={{width: '100%'}}>
                        <source src={media.src} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                ) : (
                    <img src={media.src} alt={media.alt} className="popup-image" />
                )}
                <p className="popup-caption">{media.alt}</p>
                <button className="close-button" onClick={onClose}>Close</button>
            </div>
        </div>
    );
}

export default function Gallery() {
    const [selectedMedia, setSelectedMedia] = useState(null);
    const [filter, setFilter] = useState('all');
    const [allMedia, setAllMedia] = useState([]);
    const galleryRef = useRef(null);

    const handleMediaClick = (media) => {
        setSelectedMedia(media);
    };

    const handleClosePopup = () => {
        setSelectedMedia(null);
    };

    useEffect(() => {
        const handleIntersection = (entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const filteredImages = filter === 'all' || filter === 'images' ? Object.keys(images) : [];
                    const filteredVideos = filter === 'all' || filter === 'videos' ? Object.keys(videos) : [];
                    const loadedMedia = [...filteredImages, ...filteredVideos];
                    setAllMedia(loadedMedia);
                }
            });
        };
    
        const observer = new IntersectionObserver(handleIntersection, {
            threshold: 0.1
        });
    
        // Store the current ref value in a variable
        const currentGalleryRef = galleryRef.current;
    
        if (currentGalleryRef) {
            observer.observe(currentGalleryRef);
        }
    
        return () => {
            if (currentGalleryRef) {
                observer.unobserve(currentGalleryRef);
            }
        };
    }, [filter]);

    return (
        <>
        <div className='Main-sub-heading-div bordered '>
        <h1 className='Main-sub-heading-div-h1'>
            <motion.h1
                    className="gallery-title"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    Welcome to the Amazing Gallery
            </motion.h1>
        </h1>
        <p className="gallery-description">
            Explore our collection of stunning <strong>Memories</strong> that capture the essence of life's beautiful moments. Discover vibrant images and engaging videos that tell compelling stories.
        </p>
    </div>
        <div className="gallery-page">
            <header className="gallery-header">
                <div className="filter-buttons">
                    <button 
                        className={filter === 'all' ? 'active' : ''} 
                        onClick={() => setFilter('all')}
                        style={{borderRadius: '2rem 0 0 2rem'}}
                    >
                        All
                    </button>
                    <button 
                        className={filter === 'images' ? 'active' : ''} 
                        onClick={() => setFilter('images')}
                    >
                        Images
                    </button>
                    <button 
                        className={filter === 'videos' ? 'active' : ''} 
                        onClick={() => setFilter('videos')}
                        style={{borderRadius: '0 2rem 2rem 0'}}
                    >
                        Videos
                    </button>
                </div>
            </header>

            <section className="gallery-grid" ref={galleryRef}>
                {allMedia.map((key, index) => (
                    <motion.div
                        key={index}
                        className="gallery-item"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleMediaClick({ src: images[key] || videos[key], alt: key })}
                    >
                        {images[key] ? (
                            <img src={images[key]} alt={key} className="gallery-image" />
                        ) : (
                             <video className="gallery-video" muted>
                                <source src={videos[key]} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )}
                    </motion.div>
                ))}
            </section>

            {selectedMedia && (
                <Popup media={selectedMedia} onClose={handleClosePopup} />
            )}
        </div>
        <style jsx>{`
    
    .gallery {
    color: #9a0002 !important;

    }
  `}</style>
        </>
    );
}

